import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

async function getSupabaseAdmin() {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  return supabaseAdmin;
}

async function getProfileId(userId: string): Promise<string | null> {
  const supabaseAdmin = await getSupabaseAdmin();
  const { data } = await supabaseAdmin
    .from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
  return (data?.id as string | undefined) ?? null;
}

export type WishlistProduct = {
  id: string;
  slug: string;
  title: string;
  price: number | null;
  featured_image_url: string | null;
  vendor: { store_name: string; slug: string | null } | null;
};

export const getMyWishlist = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ products: WishlistProduct[]; ids: string[] }> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const profileId = await getProfileId(context.userId);
    if (!profileId) return { products: [], ids: [] };
    const { data: items } = await supabaseAdmin
      .from("wishlist_items")
      .select("product_id")
      .eq("profile_id", profileId)
      .order("created_at", { ascending: false });
    const ids = (items ?? []).map((i) => i.product_id as string);
    if (ids.length === 0) return { products: [], ids: [] };
    const { data: products } = await supabaseAdmin
      .from("products")
      .select("id, slug, title, price, featured_image_url, vendor:vendors(store_name, slug)")
      .in("id", ids)
      .eq("status", "publish");
    return { products: (products ?? []) as WishlistProduct[], ids };
  });

export const getMyWishlistIds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ ids: string[] }> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const profileId = await getProfileId(context.userId);
    if (!profileId) return { ids: [] };
    const { data } = await supabaseAdmin
      .from("wishlist_items").select("product_id").eq("profile_id", profileId);
    return { ids: (data ?? []).map((r) => r.product_id as string) };
  });

export const toggleWishlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ product_id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }): Promise<{ in_wishlist: boolean }> => {
    const supabaseAdmin = await getSupabaseAdmin();
    const profileId = await getProfileId(context.userId);
    if (!profileId) throw new Error("Profil ne obstaja.");
    const { data: existing } = await supabaseAdmin
      .from("wishlist_items").select("id").eq("profile_id", profileId).eq("product_id", data.product_id).maybeSingle();
    if (existing) {
      await supabaseAdmin.from("wishlist_items").delete().eq("id", existing.id);
      return { in_wishlist: false };
    }
    await supabaseAdmin.from("wishlist_items").insert({ profile_id: profileId, product_id: data.product_id });
    return { in_wishlist: true };
  });
